import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 5000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

// Clear stale timeouts when page becomes visible (mobile browser handling)
let visibilityHandler: (() => void) | null = null

const addToRemoveQueue = (toastId: string, toastType?: string, message?: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  // Dynamic delay based on toast type and message length
  const isMobile = window.innerWidth < 768
  const messageLength = message?.length || 0
  
  let delay: number
  if (toastType === 'destructive' || toastType === 'error') {
    delay = Math.max(6000, Math.min(8000, messageLength * 100)) // 6-8s for errors
  } else if (toastType === 'success') {
    delay = Math.max(4000, Math.min(5000, messageLength * 80)) // 4-5s for success
  } else {
    delay = Math.max(5000, Math.min(6000, messageLength * 90)) // 5-6s for info/default
  }
  
  // Reduce by 1s on mobile but maintain minimums
  if (isMobile) {
    delay = Math.max(delay - 1000, toastType === 'destructive' ? 5000 : 3000)
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, delay)

  toastTimeouts.set(toastId, timeout)
}

// Cleanup function for timeouts
const clearAllTimeouts = () => {
  toastTimeouts.forEach(timeout => clearTimeout(timeout))
  toastTimeouts.clear()
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId) {
        // Clear the timeout immediately for this specific toast
        const timeout = toastTimeouts.get(toastId)
        if (timeout) {
          clearTimeout(timeout)
          toastTimeouts.delete(toastId)
        }
        // Then schedule removal from DOM
        const toast = state.toasts.find(t => t.id === toastId)
        addToRemoveQueue(toastId, toast?.variant, toast?.description?.toString())
      } else {
        // Clear all timeouts when dismissing all toasts
        state.toasts.forEach((toast) => {
          const timeout = toastTimeouts.get(toast.id)
          if (timeout) {
            clearTimeout(timeout)
            toastTimeouts.delete(toast.id)
          }
          addToRemoveQueue(toast.id, toast.variant, toast.description?.toString())
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // Auto-dismiss with dynamic timing
  addToRemoveQueue(id, props.variant, props.description?.toString())

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
      // Only cleanup on unmount, not on every state change
    }
  }, [])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
    clearAll: () => {
      clearAllTimeouts()
      dispatch({ type: "REMOVE_TOAST", toastId: undefined })
    },
  }
}

export { useToast, toast }
